import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { convertCurrency, type Currency } from "@/lib/currency-conversion";
import { z } from "zod";

const paymentRecordSchema = z.object({
  planName: z.string().trim().min(1, "Service/product name is required").max(200, "Name too long"),
  price: z.string().refine((val) => !isNaN(Number(val)) && Number(val) > 0, "Price must be a positive number"),
  clientName: z.string().trim().min(1, "Client name is required").max(100, "Client name too long"),
  clientEmail: z.string().email("Invalid email address").max(255, "Email too long"),
  clientPhone: z.string().max(20, "Phone number too long").optional(),
  transactionId: z.string().max(100, "Transaction ID too long").optional(),
  currency: z.string(),
  paymentType: z.enum(["one-time", "recurring"]),
});
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Receipt } from "lucide-react";

const CURRENCIES = [
  { value: "MAD", label: "MAD (د.م.)" },
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
];

export function UnifiedPaymentDialog() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const [planName, setPlanName] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<Currency>("MAD");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [paymentType, setPaymentType] = useState<"one-time" | "recurring">("one-time");

  const resetForm = () => {
    setPlanName("");
    setPrice("");
    setCurrency("MAD");
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setTransactionId("");
    setPaymentType("one-time");
  };

  const handleManualPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    try {
      paymentRecordSchema.parse({
        planName,
        price,
        clientName,
        clientEmail,
        clientPhone,
        transactionId,
        currency,
        paymentType,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }
    
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to record payments",
          variant: "destructive",
        });
        return;
      }

      const priceAmount = parseFloat(price);
      // Convert entered amount to USD for storage (USD is base currency)
      const priceInUSD = convertCurrency(priceAmount, currency, "USD");

      const { error } = await supabase.from("subscriptions").insert({
        plan_name: planName,
        price: priceInUSD,
        currency: "USD", // Always store in USD
        payment_status: "completed",
        payment_type: paymentType,
        transaction_id: transactionId || `MANUAL-${Date.now()}`,
        client_name: clientName,
        client_email: clientEmail,
        client_phone: clientPhone,
        user_id: user.id,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Payment recorded: ${currency} ${priceAmount.toFixed(2)}`,
      });

      resetForm();
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    } catch (error: any) {
      console.error("Error recording manual payment:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to record payment",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Record Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record a payment received from a client
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleManualPayment} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="planName">Service/Product Name *</Label>
            <Input
              id="planName"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g., Website Development"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={(val) => setCurrency(val as Currency)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((curr) => (
                    <SelectItem key={curr.value} value={curr.value}>
                      {curr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="price">Amount *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="1000.00"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentType">Payment Type</Label>
            <Select value={paymentType} onValueChange={(val) => setPaymentType(val as "one-time" | "recurring")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="one-time">One-Time Payment</SelectItem>
                <SelectItem value="recurring">Recurring Payment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="transactionId">Transaction/Reference ID</Label>
            <Input
              id="transactionId"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="TXN-12345 or Bank Transfer Ref"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientName">Client Name *</Label>
            <Input
              id="clientName"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientEmail">Client Email *</Label>
            <Input
              id="clientEmail"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="john@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientPhone">Client Phone</Label>
            <Input
              id="clientPhone"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              placeholder="+212 6 12 34 56 78"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              "Recording..."
            ) : (
              <>
                <Receipt className="mr-2 h-4 w-4" />
                Record Payment
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
